// Montagem e persistência da rota de visita a inadimplentes.
//
// A tela escolhe as UCs; aqui acontece o resto: resolver a coordenada de quem
// ainda não tem, ordenar as paradas e gravar. A UC é a parada — não o cliente —
// porque o endereço é dela e um cliente pode ter UCs em cidades diferentes.

import { supabase } from './supabase';
import { otimizarPontos, type Origem } from './rotaInteligente';
import {
    chaveCacheGeocode, consultaGeocodeUc, temCoordenadaConfiavel, urlNavegacaoUc, type UcEndereco,
} from './enderecoUc';
import type { AppNavegacao } from './navegacaoMaps';
import { termoMaisRelevante, type ItemTermo, type TermoAdesao } from './termoUc';
import {
    derivarStatusBoleto, valorDevido, STATUS_FATURA_COM_BOLETO, STATUS_SEM_COBRANCA, type DadosStatusBoleto, type StatusBoleto,
} from './statusBoleto';
import { ESTADOS_PENDENTES } from './parcelamentoRegras';
import { isoLocal, type AcordoLido } from './situacaoAcordo';
import { normalizeUcCodigo } from './ucCodigo';
import { normalizarCpfCnpj } from './cobrancaCliente';
import {
    agruparPorCliente, camposDoGrupo, juncoesPendentes, posicoesAposReordenar, renumerar,
    tirarUcDaParada, ucsDaParada,
    type CamposGrupo, type ParadaAgrupavel, type UcAgrupada,
} from './agrupamentoRota';
import type { UcInadimplente } from '../../views/CRM/Inadimplencia/types';
// Só tipos: `conferenciaRota` importa `FaturaEmAberto` daqui, e um import de
// VALOR nos dois sentidos fecharia um ciclo. `import type` some na compilação.
import type {
    AnexosMaterial, EnderecoSituacao, MaterialSituacao, TermoSituacao,
} from './conferenciaRota';

/** Teto da edge `geocode-search` (action geocode_ucs). Manter os dois iguais. */
export const MAX_UCS_POR_GEOCODE = 30;
/** Acima disso a Google Routes não ordena e a sequência sai do 2-opt local. */
export const MAX_PARADAS_ROUTES = 24;

/** O que a edge devolve por UC. `label` é o endereço canônico do geocoder —
 *  destino do link de navegação, ver `urlNavegacaoUc`. */
interface NivelResolvido {
    lat: number | null;
    lng: number | null;
    geocode_status: string;
    label?: string | null;
}

export interface ResultadoGeocode {
    consultados: number;
    gravadas: number;
    custoUsd: number;
    /** id da UC -> nível resolvido, para atualizar a tela sem reler tudo. */
    porUc: Map<number, NivelResolvido>;
    /** consulta -> nível resolvido. É o único casamento possível para UC sem
     *  cadastro, que não tem id para indexar. */
    porConsulta: Map<string, NivelResolvido>;
}

/**
 * Mensagem de erro de uma edge function.
 *
 * `getFunctionErrorMessage` devolve `body.error`, que nas nossas functions novas
 * é um CÓDIGO ('limite_diario', 'too_many'). O texto que serve para o operador
 * está em `body.message` — por isso a leitura aqui é própria.
 */
const mensagemDaEdge = async (error: any, padrao: string): Promise<string> => {
    try {
        const corpo = await error?.context?.clone?.().json?.();
        if (corpo?.message) return String(corpo.message);
        if (corpo?.error) return String(corpo.error);
    } catch { /* corpo não-JSON ou já consumido */ }
    return error?.message || padrao;
};

/** As UCs que ainda não têm coordenada boa E têm endereço para consultar. */
export const ucsQuePrecisamGeocode = (ucs: UcInadimplente[]): UcInadimplente[] =>
    ucs.filter(uc =>
        !temCoordenadaConfiavel(uc.cadastro)
        // `ucId != null` era exigido aqui e excluia 19 das 417 UCs com fatura
        // vencida: as que nao existem em `unidades_consumidoras` e cujo endereco
        // vem da FATURA. Elas ficavam sem localizacao para sempre, e o gestor
        // topou em duas na primeira rota. Agora a edge tambem grava no cache
        // `geocode_enderecos`, que e chaveado pela consulta e nao pela UC.
        && consultaGeocodeUc(uc.cadastro, uc.faturas[0]).length >= 3);

/** Uma linha do cache `geocode_enderecos`, já do jeito que o cadastro usa. */
interface CacheGeocode {
    lat: number | null;
    lng: number | null;
    nivel: string;
    label: string | null;
}

/**
 * Lê o cache de endereços, indexado pela chave NORMALIZADA.
 *
 * A chave crua é a string que foi ao Google no dia, com a caixa e os acentos que
 * o cadastro tinha. Procurar por igualdade exata deixava de fora endereços já
 * pagos e guardados — ver `chaveCacheGeocode`.
 */
const lerCacheGeocode = async (consultas: string[]): Promise<Map<string, CacheGeocode>> => {
    const achado = new Map<string, CacheGeocode>();
    const chaves = Array.from(new Set(consultas.map(chaveCacheGeocode).filter(c => c.length >= 3)));
    // Em blocos: `.in()` com centenas de enderecos longos estoura o tamanho da URL.
    for (let i = 0; i < chaves.length; i += 100) {
        const { data, error } = await supabase
            .from('geocode_enderecos')
            .select('consulta_norm, latitude, longitude, geocode_status, formatted')
            .in('consulta_norm', chaves.slice(i, i + 100));
        // Cache indisponível não pode derrubar quem chamou: devolve o que deu.
        if (error) { console.error('cache de geocode indisponivel', error.message); return achado; }
        (data ?? []).forEach((r: any) => achado.set(r.consulta_norm, {
            lat: r.latitude, lng: r.longitude, nivel: r.geocode_status, label: r.formatted ?? null,
        }));
    }
    return achado;
};

/**
 * Completa o cadastro com a coordenada que já está no cache.
 *
 * A conferência lia só `unidades_consumidoras`, e havia UC com endereço completo
 * na tela, coordenada paga no cache e "Falta a localização desta UC" em vermelho
 * — porque a coluna do cadastro estava vazia. Agora a conferência enxerga a mesma
 * memória que a montagem da rota usa.
 *
 * O nível vem junto ('rua', 'endereco'): é ele que decide se dá para navegar pela
 * coordenada ou se ela só serve para mostrar o mapa e a pessoa arrastar o pino.
 */
export const hidratarCadastrosPeloCache = async (
    porId: Map<number, UcEndereco>,
): Promise<Map<number, UcEndereco>> => {
    const consultaPorId = new Map<number, string>();
    porId.forEach((uc, id) => {
        if (uc?.latitude != null && uc?.longitude != null) return;
        const consulta = consultaGeocodeUc(uc);
        if (consulta.length >= 3) consultaPorId.set(id, consulta);
    });
    if (consultaPorId.size === 0) return porId;

    const achado = await lerCacheGeocode(Array.from(consultaPorId.values()));
    if (achado.size === 0) return porId;

    const saida = new Map(porId);
    consultaPorId.forEach((consulta, id) => {
        const r = achado.get(chaveCacheGeocode(consulta));
        if (!r || r.lat == null || r.lng == null) return;
        saida.set(id, {
            ...(porId.get(id) as UcEndereco),
            latitude: r.lat,
            longitude: r.lng,
            geocode_status: r.nivel,
            geocode_label: r.label,
        });
    });
    return saida;
};

/**
 * Preenche a coordenada de quem NAO tem cadastro, a partir do cache de enderecos.
 *
 * Sem isto, UC sem cadastro voltaria para a API paga a cada rota montada: nao ha
 * onde marcar "ja geocodificada" no cadastro dela, porque cadastro nao existe.
 * O cache e a memoria dessas UCs.
 */
export const hidratarCacheGeocode = async (ucs: UcInadimplente[]): Promise<UcInadimplente[]> => {
    const porConsulta = new Map<string, string[]>();
    ucs.forEach(uc => {
        if (temCoordenadaConfiavel(uc.cadastro)) return;
        const consulta = consultaGeocodeUc(uc.cadastro, uc.faturas[0]);
        if (consulta.length < 3) return;
        const lista = porConsulta.get(consulta) ?? [];
        lista.push(uc.chave);
        porConsulta.set(consulta, lista);
    });
    if (porConsulta.size === 0) return ucs;

    const achado = await lerCacheGeocode(Array.from(porConsulta.keys()));
    if (achado.size === 0) return ucs;

    const porChave = new Map<string, CacheGeocode>();
    porConsulta.forEach((listaChaves, consulta) => {
        const r = achado.get(chaveCacheGeocode(consulta));
        if (r) listaChaves.forEach(chave => porChave.set(chave, r));
    });

    return ucs.map(uc => {
        const r = porChave.get(uc.chave);
        if (!r || r.lat == null || r.lng == null) return uc;
        const cadastro = {
            ...(uc.cadastro ?? {}),
            latitude: r.lat, longitude: r.lng, geocode_status: r.nivel, geocode_label: r.label,
        } as any;
        return {
            ...uc,
            cadastro,
            latitude: r.lat,
            longitude: r.lng,
            geocodeStatus: r.nivel,
            coordenadaConfiavel: temCoordenadaConfiavel(cadastro),
        };
    });
};

/**
 * Resolve e GRAVA a coordenada das UCs selecionadas.
 *
 * Só entra aqui quem precisa: UC já geocodificada não volta para a API paga
 * (a edge grava no cadastro justamente para isso). A consulta é montada aqui,
 * pelo `consultaGeocodeUc` — que é a versão testada da limpeza de endereço.
 */
export const geocodificarUcs = async (ucs: UcInadimplente[]): Promise<ResultadoGeocode> => {
    const pendentes = ucsQuePrecisamGeocode(ucs);
    const porUc = new Map<number, NivelResolvido>();
    const porConsulta = new Map<string, NivelResolvido>();
    if (pendentes.length === 0) return { consultados: 0, gravadas: 0, custoUsd: 0, porUc, porConsulta };

    let consultados = 0;
    let gravadas = 0;
    let custoUsd = 0;

    for (let i = 0; i < pendentes.length; i += MAX_UCS_POR_GEOCODE) {
        const bloco = pendentes.slice(i, i + MAX_UCS_POR_GEOCODE);
        const { data, error } = await supabase.functions.invoke('geocode-search', {
            body: {
                action: 'geocode_ucs',
                escopo: 'cobranca',
                ucs: bloco.map(uc => ({
                    id: uc.ucId,
                    consulta: consultaGeocodeUc(uc.cadastro, uc.faturas[0]),
                    uf: uc.cadastro?.estado || uc.faturas[0]?.end_uf || null,
                    cidade: uc.cidade,
                })),
            },
        });
        if (error) throw new Error(await mensagemDaEdge(error, 'Falha ao geocodificar os endereços.'));

        consultados += Number(data?.consultados || 0);
        gravadas += Number(data?.gravadas || 0);
        custoUsd += Number(data?.custo_estimado_usd || 0);
        (data?.resultados || []).forEach((r: any) => {
            const valor: NivelResolvido = {
                lat: r.lat ?? null,
                lng: r.lng ?? null,
                geocode_status: r.geocode_status || 'falhou',
                label: r.label ?? null,
            };
            if (r?.id != null) porUc.set(Number(r.id), valor);
            // UC sem cadastro so pode ser reencontrada pela consulta.
            if (r?.consulta) porConsulta.set(String(r.consulta), valor);
        });
    }

    return { consultados, gravadas, custoUsd, porUc, porConsulta };
};

/**
 * Localiza, pela API paga, as paradas da rota que estão sem pino confiável.
 *
 * É a mesma chamada da montagem (`geocodificarUcs`, teto diário no servidor),
 * só que a partir da rota já salva: a parada que entrou como extra, ou que a
 * montagem não conseguiu localizar naquele dia, ficava "sem localização" até
 * alguém remontar a rota. Só vai quem tem cadastro e endereço para consultar —
 * sem cadastro não há onde gravar, e aí o caminho é corrigir no card.
 */
export const localizarParadasSemPino = async (
    paradas: ParadaSalva[],
    cadastros: Map<number, UcEndereco>,
): Promise<ResultadoGeocode> => {
    const ucs = paradas.flatMap(p => {
        const id = p.unidade_consumidora_id;
        const cadastro = id != null ? cadastros.get(id) : undefined;
        if (id == null || !cadastro) return [];
        // Só os campos que `geocodificarUcs` lê. O resto de UcInadimplente é da
        // Central (valores, faturas) e não entra na consulta.
        return [{
            chave: String(id),
            ucId: id,
            cidade: cadastro.cidade || '',
            faturas: [],
            cadastro,
        } as unknown as UcInadimplente];
    });
    return geocodificarUcs(ucs);
};

/**
 * Grava a localização que alguém escolheu para a parada, pelo endereço digitado.
 *
 * Na parada (como o pino corrigido da conferência) e no cadastro da UC, marcada
 * como 'manual': o endereço errado é do cadastro, e a próxima rota com essa UC
 * não pode errar de novo. Mexe só nos campos de endereço — `conferirParada`
 * regravaria também material e termo.
 */
export const corrigirLocalizacaoParada = async (
    paradaId: string,
    ucId: number | null,
    ponto: { lat: number; lng: number; label: string | null },
): Promise<void> => {
    const { error } = await supabase
        .from('rota_cobranca_paradas')
        .update({
            endereco_situacao: 'corrigido',
            endereco_lat: ponto.lat,
            endereco_lng: ponto.lng,
            endereco_label: ponto.label,
        })
        .eq('id', paradaId);
    if (error) throw error;
    if (ucId != null) await corrigirCoordenadaUc(ucId, ponto.lat, ponto.lng, ponto.label);
};

/** Aplica no objeto em memória o que a edge acabou de gravar no banco. */
export const aplicarGeocode = (uc: UcInadimplente, r: ResultadoGeocode): UcInadimplente => {
    // Por id quando ha cadastro; pela consulta quando nao ha — que e o caso das
    // UCs cujo endereco vem so da fatura.
    const novo = uc.ucId != null
        ? r.porUc.get(uc.ucId)
        : r.porConsulta.get(consultaGeocodeUc(uc.cadastro, uc.faturas[0]));
    if (!novo) return uc;
    // O objeto nasce mesmo sem cadastro: UC que só existe na fatura também
    // precisa da coordenada em mãos para entrar na ordenação desta rota — sem
    // isto ela continuaria "sem localização" até a próxima montagem, quando o
    // cache a resgataria. Mesma construção de `hidratarCacheGeocode`.
    const cadastro = {
        ...(uc.cadastro ?? {}),
        latitude: novo.lat,
        longitude: novo.lng,
        geocode_status: novo.geocode_status,
        geocode_label: novo.label ?? null,
    } as UcInadimplente['cadastro'];
    return {
        ...uc,
        cadastro,
        latitude: novo.lat,
        longitude: novo.lng,
        geocodeStatus: novo.geocode_status,
        coordenadaConfiavel: temCoordenadaConfiavel(cadastro),
    };
};

export interface ParadaPrevia {
    posicao: number;
    /** A UC principal — a do endereço onde se vai. */
    uc: UcInadimplente;
    /** Outras UCs do mesmo CPF/CNPJ cobradas nesta mesma porta. Ver agrupamentoRota.ts. */
    extras: UcInadimplente[];
    urlMaps: string;
}

/** Total a cobrar na parada: a principal mais as agrupadas. */
export const valorDaParada = (p: Pick<ParadaPrevia, 'uc' | 'extras'>): number =>
    p.uc.valorTotal + p.extras.reduce((s, uc) => s + uc.valorTotal, 0);

export const faturasDaParada = (p: Pick<ParadaPrevia, 'uc' | 'extras'>): number =>
    p.uc.qtdFaturas + p.extras.reduce((s, uc) => s + uc.qtdFaturas, 0);

export interface PreviaRotaCobranca {
    paradas: ParadaPrevia[];
    /** UCs escolhidas que não puderam ser ordenadas por falta de coordenada. */
    semCoordenada: UcInadimplente[];
    distanciaKm: number;
    duracaoMin: number | null;
    fonte: 'google' | 'local';
    origem: Origem | null;
    origemLabel: string | null;
    /** Ponto de chegada. Nulo = a rota termina na última parada. */
    destino: Origem | null;
    destinoLabel: string | null;
    /** Quantas UCs entraram como agrupadas em vez de virar parada própria. */
    ucsJuntadas: number;
}

export interface OpcoesPrevia {
    origem: Origem | null;
    origemLabel: string | null;
    destino?: Origem | null;
    destinoLabel?: string | null;
    /** Em qual app os links de cada parada devem abrir — ver navegacaoMaps.ts. */
    app?: AppNavegacao;
    /** Junta as UCs do mesmo CPF/CNPJ numa parada. Padrão: sim. */
    agrupar?: boolean;
}

/**
 * Ordena as paradas e devolve a prévia. NADA é gravado aqui.
 *
 * As UCs sem coordenada confiável não são descartadas: elas saem da ordenação
 * (não há como ordená-las) mas entram no fim da rota, para o cobrador decidir.
 * Sumir com elas em silêncio seria pior — são justamente os endereços que ele
 * não consegue achar, o motivo de tudo isso existir.
 *
 * O agrupamento vem ANTES da ordenação: o otimizador recebe uma porta por
 * cliente, então as UCs juntadas não pesam na distância.
 */
export const montarPreviaRota = async (
    ucs: UcInadimplente[],
    opcoes: OpcoesPrevia,
): Promise<PreviaRotaCobranca> => {
    const { origem, origemLabel, destino = null, destinoLabel = null, app = 'google', agrupar = true } = opcoes;

    const grupos = agrupar
        ? agruparPorCliente(ucs)
        : ucs.map(uc => ({ principal: uc, extras: [] as UcInadimplente[] }));
    const extrasPorChave = new Map(grupos.map(g => [g.principal.chave, g.extras]));
    const principais = grupos.map(g => g.principal);

    const comCoord = principais.filter(uc => uc.coordenadaConfiavel && uc.latitude != null && uc.longitude != null);
    const semCoordenada = principais.filter(uc => !comCoord.includes(uc));

    let ordenadas: UcInadimplente[] = [];
    let distanciaKm = 0;
    let duracaoMin: number | null = null;
    let fonte: 'google' | 'local' = 'local';

    if (comCoord.length > 0) {
        const pontos = comCoord.map(uc => ({
            id: uc.chave,
            lat: Number(uc.latitude),
            lng: Number(uc.longitude),
            label: uc.clienteNome,
            uc,
        }));
        const res = await otimizarPontos(pontos, origem ?? undefined, 'cobranca', destino);
        ordenadas = res.order.map(p => p.uc);
        distanciaKm = res.totalKm;
        duracaoMin = res.duracaoMin;
        fonte = res.fonte;
    }

    const todas = [...ordenadas, ...semCoordenada];
    return {
        paradas: todas.map((uc, i) => ({
            posicao: i + 1,
            uc,
            extras: extrasPorChave.get(uc.chave) ?? [],
            urlMaps: urlNavegacaoUc(uc.cadastro, uc.faturas[0], app),
        })),
        semCoordenada,
        distanciaKm,
        duracaoMin,
        fonte,
        origem,
        origemLabel,
        destino,
        destinoLabel: destino ? destinoLabel : null,
        ucsJuntadas: grupos.reduce((s, g) => s + g.extras.length, 0),
    };
};

/** A UC da tela no formato do jsonb `ucs_agrupadas`. */
const paraUcAgrupada = (uc: UcInadimplente): UcAgrupada => ({
    unidade_consumidora_id: uc.ucId,
    uc_codigo: uc.ucCodigo,
    cliente_nome: uc.clienteNome,
    qtd_faturas: uc.qtdFaturas,
    valor_total: Number(uc.valorTotal.toFixed(2)),
});

export interface DadosSalvarRota {
    /** Quem vai EXECUTAR a rota. Pode ser outra pessoa — ver `criadaPor`. */
    responsavelId: string;
    /** Quem está montando. Separado de `responsavelId` desde 14/09/2026: antes as
     *  duas coisas eram a mesma coluna e não dava para mandar rota para alguém
     *  com rastro de quem mandou. */
    criadaPor: string;
    data: string;
    cidade?: string | null;
    nome?: string | null;
}

/**
 * A rota igual a esta que já existe hoje, se existir.
 *
 * Existe porque a lista do gestor encheu de duplicatas — "Cobrança GOIÂNIA" duas
 * vezes em 11/09, "Cobrança geral" três vezes em 10/09. A causa: gerar a prévia
 * limpa o `rotaId`, então clicar em "Gerar rota otimizada" de novo e salvar cria
 * outra rota com as MESMAS paradas. Ninguém quis duas; foi o botão.
 *
 * "Igual" é: mesmo responsável, mesmo dia, exatamente o mesmo conjunto de UCs, e
 * ainda viva (conferência ou planejada). Rota em execução ou concluída não é
 * reaproveitada — ali as paradas já têm resultado e sobrescrever perderia visita.
 */
const rotaIdenticaDeHoje = async (
    previa: PreviaRotaCobranca,
    dados: DadosSalvarRota,
): Promise<string | null> => {
    const { data, error } = await supabase
        .from('rotas_cobranca')
        .select('id, rota_cobranca_paradas(uc_codigo, ucs_agrupadas, status)')
        .eq('responsavel_id', dados.responsavelId)
        .eq('data', dados.data)
        .in('status', ['em_conferencia', 'planejada']);
    // Duplicata é incômodo, não corrupção: se a checagem falhar, seguir e gravar
    // é melhor do que impedir a pessoa de montar a rota.
    if (error || !data) return null;

    // Compara o conjunto de UCs, não de paradas: a mesma seleção montada com e
    // sem junção por cliente é a mesma rota para quem vai cobrar.
    const alvo = previa.paradas
        .flatMap(p => [p.uc.ucCodigo, ...p.extras.map(e => e.ucCodigo)])
        .sort().join('|');
    const igual = data.find((r: any) => {
        const codigos = (r.rota_cobranca_paradas || [])
            .filter((p: any) => p.status !== 'removida')
            .flatMap((p: any) => [
                p.uc_codigo,
                ...(Array.isArray(p.ucs_agrupadas) ? p.ucs_agrupadas.map((u: any) => u.uc_codigo) : []),
            ])
            .sort().join('|');
        return codigos === alvo && codigos.length > 0;
    });
    return igual ? String((igual as any).id) : null;
};

/**
 * Grava a rota e suas paradas. Devolve o id da rota.
 *
 * A ROTA VEM PRIMEIRO, de propósito. Na Prospecção esse insert era o último e,
 * quando ele falhava, os registros criados antes ficavam órfãos — três cliques
 * no botão deixaram 30 negociações e 30 tarefas sem rota. Aqui, se a rota não
 * nasce, nada mais é gravado.
 */
export const salvarRotaCobranca = async (
    previa: PreviaRotaCobranca,
    dados: DadosSalvarRota,
): Promise<string> => {
    const existente = await rotaIdenticaDeHoje(previa, dados);
    if (existente) return existente;

    const { data: rota, error: erroRota } = await supabase
        .from('rotas_cobranca')
        .insert({
            responsavel_id: dados.responsavelId,
            criada_por: dados.criadaPor,
            data: dados.data,
            nome: dados.nome ?? null,
            cidade: dados.cidade ?? null,
            // A rota NASCE em conferência. Só vira 'planejada' quando quem montou
            // confirmar endereço, material e termo de cada parada — até lá a tela
            // de execução não mostra os dados de cobrança para o cobrador.
            status: 'em_conferencia',
            origem_lat: previa.origem?.lat ?? null,
            origem_lng: previa.origem?.lng ?? null,
            origem_label: previa.origemLabel ?? null,
            destino_lat: previa.destino?.lat ?? null,
            destino_lng: previa.destino?.lng ?? null,
            destino_label: previa.destinoLabel ?? null,
            distancia_km: Number(previa.distanciaKm.toFixed(1)),
            duracao_min: previa.duracaoMin,
            snapshot: {
                versao: 2,
                gerada_em: new Date().toISOString(),
                fonte: previa.fonte,
                origin: previa.origem,
                destino: previa.destino,
                total_km: Number(previa.distanciaKm.toFixed(1)),
                duracao_min: previa.duracaoMin,
                sem_coordenada: previa.semCoordenada.length,
                ucs_juntadas: previa.ucsJuntadas,
                ordem: previa.paradas.map(p => ({
                    posicao: p.posicao,
                    uc_codigo: p.uc.ucCodigo,
                    agrupadas: p.extras.map(e => e.ucCodigo),
                    lat: p.uc.latitude,
                    lng: p.uc.longitude,
                })),
            },
        })
        .select('id')
        .single();

    if (erroRota) throw erroRota;
    const rotaId = String(rota.id);

    // Valor e quantidade são congelados aqui: a rota é o retrato do dia. Se o
    // cliente pagar entre a geração e a visita, a tela de execução relê o saldo
    // atual e mostra a diferença, em vez de o cobrador chegar com número errado.
    // Numa parada com UCs juntadas, os dois são o TOTAL — é o que se cobra ali.
    const { error: erroParadas } = await supabase.from('rota_cobranca_paradas').insert(
        previa.paradas.map(p => ({
            rota_id: rotaId,
            posicao: p.posicao,
            cpf_cnpj_cliente: p.uc.cpfCnpj,
            status: 'pendente',
            ...camposDoGrupo([p.uc, ...p.extras].map(paraUcAgrupada)),
        })),
    );
    if (erroParadas) throw erroParadas;

    return rotaId;
};

export interface EventoTratativa {
    em: string | null;
    titulo: string | null;
    detalhe: string | null;
    autor: string | null;
}

/**
 * A tratativa mais recente de cada cliente, para a linha de histórico da
 * mensagem — o "foi tentado tratar assim" que o gestor pediu.
 *
 * Usa a RPC `timeline_cobranca_cliente`, que já une as cinco fontes de histórico
 * de cobrança (negociação, contato, régua automática, fila humana, notificação
 * extrajudicial e comunicado). Uma chamada por CPF, em blocos, porque a RPC
 * recebe um documento por vez.
 */
export const ultimaTratativaPorCpf = async (
    cpfs: Array<string | null | undefined>,
): Promise<Map<string, EventoTratativa>> => {
    const unicos = Array.from(new Set(cpfs.map(c => String(c || '').replace(/\D/g, '')).filter(Boolean)));
    const mapa = new Map<string, EventoTratativa>();

    const BLOCO = 6;
    for (let i = 0; i < unicos.length; i += BLOCO) {
        const bloco = unicos.slice(i, i + BLOCO);
        await Promise.all(bloco.map(async cpf => {
            const { data, error } = await supabase.rpc('timeline_cobranca_cliente', { p_cpf_cnpj: cpf });
            // Histórico é enfeite da mensagem: se falhar, a linha some e a rota
            // continua. Derrubar a geração por causa disso seria desproporcional.
            if (error || !Array.isArray(data) || data.length === 0) return;
            const topo = data[0] as any;   // a RPC já ordena do mais recente
            mapa.set(cpf, {
                em: topo.em ?? null,
                titulo: topo.titulo ?? null,
                detalhe: topo.detalhe ?? null,
                autor: topo.autor ?? null,
            });
        }));
    }
    return mapa;
};

export interface ParadaSalva {
    id: string;
    posicao: number;
    unidade_consumidora_id: number | null;
    uc_codigo: string;
    cliente_nome: string | null;
    cpf_cnpj_cliente: string | null;
    qtd_faturas: number | null;
    valor_total: number | null;
    status: string;
    resultado: string | null;
    observacao: string | null;
    visitado_em: string | null;
    /**
     * Dia (AAAA-MM-DD) em que a parada reagendada volta para a lista. Ela segue
     * pendente; antes do dia fica fora da vista. Nulo = não reagendada.
     */
    reagendada_para?: string | null;
    // Conferência — ver conferenciaRota.ts. Nulo = ainda não conferida.
    endereco_situacao: EnderecoSituacao | null;
    endereco_lat: number | null;
    endereco_lng: number | null;
    endereco_label: string | null;
    material_situacao: MaterialSituacao | null;
    material_faltando: unknown;
    material_justificativa: string | null;
    material_anexos: AnexosMaterial | null;
    termo_situacao: TermoSituacao | null;
    termo_url: string | null;
    conferida_em: string | null;
    conferida_por: string | null;
    /** Outras UCs do mesmo cliente cobradas nesta parada. Ver agrupamentoRota.ts. */
    ucs_agrupadas: UcAgrupada[] | null;
    adicionada_por?: string | null;
    removida_em?: string | null;
    removida_por?: string | null;
    removida_motivo?: string | null;
}

export interface RotaSalva {
    id: string;
    data: string;
    nome: string | null;
    cidade: string | null;
    status: string;
    distancia_km: number | null;
    duracao_min: number | null;
    origem_label: string | null;
    origem_lat: number | null;
    origem_lng: number | null;
    /** Ponto de chegada. Nulo = a rota termina na última parada. */
    destino_lat: number | null;
    destino_lng: number | null;
    destino_label: string | null;
    responsavel_id: string | null;
    /** Nome de quem faz a rota. Só é lido na visão do time — na rota própria
     *  seria redundante, e a tela usa a presença dele para decidir se mostra. */
    responsavel_nome: string | null;
    /** Quem montou. Aparece sempre, inclusive na rota própria: o cobrador precisa
     *  saber a quem perguntar quando o endereço ou o boleto estiver errado. */
    criada_por: string | null;
    criador_nome: string | null;
    liberada_em: string | null;
    liberada_por: string | null;
    paradas: ParadaSalva[];
}

/** Colunas lidas nas duas consultas de rota. Uma constante só para as duas não
 *  divergirem — já aconteceu de a tela de execução não ter um campo que a
 *  listagem tinha. Os hints `!fk` são obrigatórios: `profiles` é referenciada
 *  quatro vezes por `rotas_cobranca` e sem eles o PostgREST não sabe qual join
 *  fazer e devolve erro de relação ambígua. */
const COLUNAS_ROTA =
    'id, data, nome, cidade, status, distancia_km, duracao_min, origem_label, '
    + 'origem_lat, origem_lng, destino_lat, destino_lng, destino_label, '
    + 'responsavel_id, criada_por, liberada_em, liberada_por, '
    + 'responsavel:profiles!rotas_cobranca_responsavel_id_fkey(full_name), '
    + 'criador:profiles!rotas_cobranca_criada_por_fkey(full_name), '
    + 'rota_cobranca_paradas(*)';

/** O embed vem objeto ou array conforme o PostgREST detecta a relação — mesma
 *  armadilha de `embedToArray` em src/lib/utils.ts. */
const nomeDoEmbed = (v: any): string | null =>
    (Array.isArray(v) ? v[0]?.full_name : v?.full_name) ?? null;

const mapearRota = (r: any): RotaSalva => ({
    id: String(r.id),
    data: String(r.data),
    nome: r.nome ?? null,
    cidade: r.cidade ?? null,
    status: String(r.status),
    distancia_km: r.distancia_km ?? null,
    duracao_min: r.duracao_min ?? null,
    origem_label: r.origem_label ?? null,
    origem_lat: r.origem_lat ?? null,
    origem_lng: r.origem_lng ?? null,
    destino_lat: r.destino_lat ?? null,
    destino_lng: r.destino_lng ?? null,
    destino_label: r.destino_label ?? null,
    responsavel_id: r.responsavel_id ?? null,
    responsavel_nome: nomeDoEmbed(r.responsavel),
    criada_por: r.criada_por ?? null,
    criador_nome: nomeDoEmbed(r.criador),
    liberada_em: r.liberada_em ?? null,
    liberada_por: r.liberada_por ?? null,
    // Removida fica no banco só para auditoria (ver removerParada): para a tela,
    // contagem, Maps e conferência ela não existe mais.
    paradas: (r.rota_cobranca_paradas || [])
        .filter((p: any) => p.status !== 'removida')
        .sort((a: any, b: any) => a.posicao - b.posicao),
});

export const carregarRotaCobranca = async (rotaId: string): Promise<RotaSalva | null> => {
    const { data, error } = await supabase
        .from('rotas_cobranca')
        .select(COLUNAS_ROTA)
        .eq('id', rotaId)
        .maybeSingle();
    if (error) throw error;
    if (!data) return null;
    return mapearRota(data);
};

/** As rotas do usuário, mais recentes primeiro — para a tela de execução achar a de hoje. */
/**
 * As rotas de cobrança. Com `responsavelId`, só as daquela pessoa; com `null`,
 * as de todo mundo — a visão de quem coordena a cobrança.
 *
 * A RLS de `rotas_cobranca` é permissiva (`USING true`): quem separa é o
 * RoleGuard da rota mais a decisão da tela, como já vale no resto da cobrança.
 * Passar `null` aqui não fura permissão nenhuma — só deixa de filtrar.
 */
export const listarRotasCobranca = async (
    responsavelId: string | null,
    limite = 20,
): Promise<RotaSalva[]> => {
    let consulta = supabase
        .from('rotas_cobranca')
        .select(COLUNAS_ROTA)
        // Rota cancelada some da lista: ela só existe para não perder a visita já
        // registrada, e continuar aparecendo faria o cobrador abrir uma rota morta.
        .neq('status', 'cancelada')
        .order('data', { ascending: false })
        .limit(limite);
    if (responsavelId) consulta = consulta.eq('responsavel_id', responsavelId);

    const { data, error } = await consulta;
    if (error) throw error;
    return (data || []).map(mapearRota);
};

/** Sugestões de endereço enquanto a pessoa digita (Google Places, pela edge).
 *  Serve à partida e à chegada da Central e ao "reordenar a partir de" da rua. */
export const sugerirEnderecos = async (texto: string): Promise<Array<{ placeId: string; text: string }>> => {
    if (texto.trim().length < 3) return [];
    const { data, error } = await supabase.functions.invoke('geocode-search', {
        body: { action: 'autocomplete', input: texto, escopo: 'cobranca' },
    });
    if (error) throw error;
    return data?.suggestions || [];
};

/** A coordenada da sugestão escolhida. Null quando o Google não a localiza. */
export const localizarSugestao = async (
    placeId: string,
): Promise<(Origem & { label: string | null }) | null> => {
    const { data, error } = await supabase.functions.invoke('geocode-search', {
        body: { action: 'details', placeId, escopo: 'cobranca' },
    });
    if (error) throw error;
    if (data?.lat == null) return null;
    return { lat: data.lat, lng: data.lng, label: data.label ?? null };
};

/**
 * Reordena as paradas que faltam a partir de onde o cobrador está agora — ou
 * de um endereço que ele digitou.
 *
 * A ordem da montagem parte do escritório (ou de onde quem montou estava), e o
 * cobrador muitas vezes começa de outro lugar ou pula paradas no caminho. Só as
 * pendentes com coordenada entram no otimizador; o resto segue a regra de
 * `posicoesAposReordenar`. A chegada da rota continua sendo respeitada.
 *
 * Nunca roda sozinha — só pelo botão. As posições mudam para todo mundo que
 * abre a rota, inclusive o `?parada=N` de uma mensagem já enviada.
 */
export const reordenarRotaCobrancaAPartirDe = async (
    rota: Pick<RotaSalva, 'id' | 'destino_lat' | 'destino_lng' | 'paradas'>,
    /** Pendentes com coordenada confiável — quem decide qual vale é a tela. */
    pontos: Array<{ id: string; lat: number; lng: number }>,
    origem: Origem,
    /**
     * Onde a rota termina. Omitido, vale a chegada já gravada na rota; `null`
     * tira a chegada (termina na última parada). Até 23/09/2026 só a montagem
     * definia isso — quem voltava para casa no fim do dia não tinha como dizer.
     */
    chegada?: (Origem & { label: string | null }) | null,
): Promise<{ reordenadas: number; distanciaKm: number }> => {
    if (chegada !== undefined) {
        // Antes de reordenar: os links do Maps leem a chegada da rota, e gravar
        // depois deixaria a ordem nova com o fim antigo se a segunda escrita falhasse.
        const { error } = await supabase.from('rotas_cobranca').update({
            destino_lat: chegada?.lat ?? null,
            destino_lng: chegada?.lng ?? null,
            destino_label: chegada ? (chegada.label || 'Ponto de chegada') : null,
        }).eq('id', rota.id);
        if (error) throw error;
    }
    const destino = chegada !== undefined
        ? (chegada ? { lat: chegada.lat, lng: chegada.lng } : null)
        : rota.destino_lat != null && rota.destino_lng != null
            ? { lat: Number(rota.destino_lat), lng: Number(rota.destino_lng) }
            : null;
    const res = await otimizarPontos(pontos.map(p => ({ ...p, label: '' })), origem, 'cobranca_campo', destino);
    await gravarPosicoes(rota.id, posicoesAposReordenar(rota.paradas, res.order.map(p => p.id)));
    return { reordenadas: res.order.length, distanciaKm: res.totalKm };
};

/**
 * Põe estas pendentes no começo da fila, nesta ordem — o "Visitar N" com as
 * vizinhas escolhidas (`escolherVizinhas`). As outras seguem depois, na ordem
 * em que estavam. Sem API: a ordem já vem pronta.
 */
export const pendentesNaFrente = async (
    rota: Pick<RotaSalva, 'id' | 'paradas'>,
    ids: string[],
): Promise<void> => {
    await gravarPosicoes(rota.id, posicoesAposReordenar(rota.paradas, ids));
};

/** Em lotes: o PostgREST não tem UPDATE em lote com valores diferentes, e um
 *  a um deixaria o botão pendurado no 4G do carro. */
const gravarPosicoes = async (rotaId: string, mudancas: Array<{ id: string; posicao: number }>) => {
    for (let i = 0; i < mudancas.length; i += 20) {
        const erros = await Promise.all(mudancas.slice(i, i + 20).map(m =>
            supabase.from('rota_cobranca_paradas').update({ posicao: m.posicao })
                .eq('id', m.id).eq('rota_id', rotaId)
                .then(({ error }) => error)));
        const erro = erros.find(Boolean);
        if (erro) throw erro;
    }
};

// ---------------------------------------------------------------------------
// Conferência, liberação e exclusão
// ---------------------------------------------------------------------------

/** O cadastro de endereço de cada parada, para a conferência mostrar o pino.
 *  A parada guarda só o código da UC; o endereço e a coordenada vivem no
 *  cadastro, e é lá que a correção é gravada. */
export const cadastrosDasParadas = async (
    paradas: Array<{ id: string; unidade_consumidora_id: number | null }>,
): Promise<Map<string, UcEndereco>> => {
    const porParada = new Map<string, UcEndereco>();
    const porId = await cadastrosPorUcId(paradas.map(p => p.unidade_consumidora_id));
    paradas.forEach(p => {
        const uc = p.unidade_consumidora_id != null ? porId.get(p.unidade_consumidora_id) : undefined;
        if (uc) porParada.set(p.id, uc);
    });
    return porParada;
};

/** Cadastro de endereço por id de UC — inclui as UCs agrupadas, que não são
 *  a principal de parada nenhuma mas precisam do `outro_numero_uc`. */
export const cadastrosPorUcId = async (
    idsBrutos: Array<number | null | undefined>,
): Promise<Map<number, UcEndereco>> => {
    const ids = Array.from(new Set(idsBrutos.filter((v): v is number => v != null)));
    const porId = new Map<number, UcEndereco>();
    if (ids.length === 0) return porId;
    for (let i = 0; i < ids.length; i += 200) {
        const { data, error } = await supabase
            .from('unidades_consumidoras')
            // `outro_numero_uc` é obrigatório aqui: a Equatorial dá dois números à
            // mesma unidade e a fatura vem ora com um, ora com outro. Sem ele,
            // `faturasAtuaisDaUc` perde as faturas gravadas com a outra grafia e a
            // conferência mostraria "nada em aberto" para quem deve.
            .select('id, logradouro, numero, complemento, bairro, cidade, estado, cep, endereco, latitude, longitude, geocode_status, geocode_label, geocoded_at, whatsapp_cliente, whatsapp_cliente_2, titular_fatura, numero_uc, outro_numero_uc')
            .in('id', ids.slice(i, i + 200));
        if (error) throw error;
        (data ?? []).forEach((r: any) => porId.set(Number(r.id), r as UcEndereco));
    }
    return porId;
};

/** O que a tela de conferência grava numa parada. */
export interface DadosConferenciaParada {
    enderecoSituacao: EnderecoSituacao;
    /** Só quando `corrigido`: a coordenada que a pessoa arrastou ou colou. */
    enderecoLat?: number | null;
    enderecoLng?: number | null;
    enderecoLabel?: string | null;
    materialSituacao: MaterialSituacao;
    materialFaltando?: unknown;
    materialJustificativa?: string | null;
    materialAnexos?: AnexosMaterial | null;
    termoSituacao: TermoSituacao;
    termoUrl?: string | null;
    conferidaPor: string;
}

export const conferirParada = async (
    paradaId: string,
    d: DadosConferenciaParada,
): Promise<void> => {
    const { error } = await supabase
        .from('rota_cobranca_paradas')
        .update({
            endereco_situacao: d.enderecoSituacao,
            endereco_lat: d.enderecoLat ?? null,
            endereco_lng: d.enderecoLng ?? null,
            endereco_label: d.enderecoLabel ?? null,
            material_situacao: d.materialSituacao,
            material_faltando: d.materialFaltando ?? null,
            material_justificativa: d.materialJustificativa ?? null,
            material_anexos: d.materialAnexos ?? null,
            termo_situacao: d.termoSituacao,
            termo_url: d.termoUrl ?? null,
            conferida_em: new Date().toISOString(),
            conferida_por: d.conferidaPor,
        })
        .eq('id', paradaId);
    if (error) throw error;
};

/**
 * Grava a coordenada corrigida na UC, não só na parada.
 *
 * O endereço errado não é problema desta rota — é do cadastro. Corrigir só na
 * parada faria o próximo que montar uma rota com a mesma UC errar de novo.
 * `geocode_status='manual'` marca que foi gente, não a API: `temCoordenadaConfiavel`
 * aceita, e o `ucsQuePrecisamGeocode` para de mandá-la de volta para a API paga.
 */
export const corrigirCoordenadaUc = async (
    ucId: number,
    lat: number,
    lng: number,
    label?: string | null,
): Promise<void> => {
    const { error } = await supabase
        .from('unidades_consumidoras')
        .update({
            latitude: lat,
            longitude: lng,
            geocode_status: 'manual',
            geocode_label: label ?? null,
        })
        .eq('id', ucId);
    if (error) throw error;
};

/**
 * Sobe um PDF da conferência (fatura da Equatorial ou termo de adesão).
 *
 * Vai no bucket `faturas`, que já é privado e lido por signed URL em todo o
 * Financeiro — documento de cliente não pode ficar em bucket público. O prefixo
 * `rota-cobranca/` separa do que o robô de extração grava lá.
 *
 * Devolve o CAMINHO, não a URL: URL assinada expira, e o que se guarda no banco
 * tem que continuar resolvendo daqui a seis meses.
 */
export const anexarPdfConferencia = async (
    rotaId: string,
    paradaId: string,
    tipo: 'fatura' | 'termo',
    arquivo: File,
    /** Só para `tipo='fatura'`: qual fatura este PDF cobre. */
    faturaId?: string,
): Promise<string> => {
    const sufixo = tipo === 'fatura' ? `fatura-${faturaId ?? 'sem-id'}` : 'termo';
    // `Date.now()` no nome: reanexar não pode depender de `upsert`, senão o
    // arquivo antigo some e um anexo já referenciado por outra parada quebraria.
    const caminho = `rota-cobranca/${rotaId}/${paradaId}-${sufixo}-${Date.now()}.pdf`;

    const { error } = await supabase.storage
        .from('faturas')
        .upload(caminho, arquivo, { contentType: arquivo.type || 'application/pdf' });
    if (error) throw new Error(error.message || 'Não consegui subir o arquivo.');
    return caminho;
};

/**
 * Abre um PDF anexado na conferência.
 *
 * O bucket `faturas` é privado: o caminho gravado na parada só abre por URL
 * assinada. Cinco minutos, igual ao boleto em `linkBoleto.ts` — o link é para
 * abrir agora, não para guardar.
 *
 * A janela abre ANTES do await, ainda dentro do toque: aberta depois, o
 * navegador do celular trata como popup e bloqueia sem avisar.
 */
export const abrirAnexoConferencia = async (caminho: string): Promise<void> => {
    // O termo anexado pela tela da rota mora no bucket público do cadastro da UC
    // (ver anexarTermoNasUcs) e chega aqui como link inteiro, não como caminho.
    if (/^https?:\/\//i.test(caminho)) { window.open(caminho, '_blank'); return; }
    const janela = window.open('', '_blank');
    const { data, error } = await supabase.storage.from('faturas').createSignedUrl(caminho, 300);
    if (error || !data?.signedUrl) {
        janela?.close();
        throw new Error(error?.message || 'Não consegui abrir o anexo.');
    }
    if (janela) janela.location.href = data.signedUrl;
    else window.open(data.signedUrl, '_blank');
};

/**
 * Anexa o termo de adesão no CADASTRO das UCs, não só na parada.
 *
 * A conferência guarda o PDF na parada (`termo_situacao='anexado'`): resolve a
 * rota e some para o resto do sistema — a próxima rota, a Central e o boleto
 * continuam dizendo "sem termo". Aqui ele vai para `url_termo` da UC, no mesmo
 * bucket e formato do UCManagementModal, e passa a valer em toda parte.
 *
 * Acrescenta à lista em vez de substituir: a UC pode ter item antigo sem PDF, e
 * apagá-lo não é decisão desta tela.
 *
 * UC só da fatura (sem cadastro) não tem onde guardar: aí o termo fica na parada,
 * como na conferência, apontando para o mesmo arquivo.
 */
export const anexarTermoNasUcs = async (
    paradaId: string,
    ucs: Array<{ ucId: number | null }>,
    arquivo: File,
): Promise<{ url: string }> => {
    const ids = Array.from(new Set(ucs.map(u => u.ucId).filter((v): v is number => v != null)));
    const caminho = `documentos_uc/${ids[0] ?? `parada-${paradaId}`}_termo_${Date.now()}.pdf`;
    const { error: erroUpload } = await supabase.storage
        .from('clientes_docs')
        .upload(caminho, arquivo, { contentType: arquivo.type || 'application/pdf' });
    if (erroUpload) throw new Error(erroUpload.message || 'Não consegui subir o termo.');
    const url = supabase.storage.from('clientes_docs').getPublicUrl(caminho).data.publicUrl;
    const item = { url_pdf: url, data_criacao: new Date().toISOString() };

    for (const id of ids) {
        const { data, error } = await supabase
            .from('unidades_consumidoras').select('url_termo').eq('id', id).maybeSingle();
        if (error) throw error;
        const atual = Array.isArray((data as any)?.url_termo) ? (data as any).url_termo : [];
        const { error: e } = await supabase
            .from('unidades_consumidoras').update({ url_termo: [...atual, item] }).eq('id', id);
        if (e) throw e;
    }

    if (ucs.some(u => u.ucId == null)) {
        const { error } = await supabase.from('rota_cobranca_paradas')
            .update({ termo_situacao: 'anexado', termo_url: url }).eq('id', paradaId);
        if (error) throw error;
    }
    return { url };
};

/** Libera a rota para o cobrador. A tela só chama isto com `situacaoDaRota().liberavel`. */
export const liberarRotaCobranca = async (rotaId: string, userId: string): Promise<void> => {
    const { error } = await supabase
        .from('rotas_cobranca')
        .update({ status: 'planejada', liberada_em: new Date().toISOString(), liberada_por: userId })
        .eq('id', rotaId);
    if (error) throw error;
};

// ---------------------------------------------------------------------------
// Mexer nas paradas antes de liberar
//
// Pedido de 16/09/2026: não havia como tirar uma UC da rota na conferência —
// quem achava um cliente que não devia ir tinha que apagar a rota inteira e
// montar de novo. Tudo aqui só vale com a rota em `em_conferencia`: depois de
// liberada, a mensagem já saiu com os números das paradas.
// ---------------------------------------------------------------------------

const exigirEmConferencia = async (rotaId: string): Promise<void> => {
    const { data, error } = await supabase
        .from('rotas_cobranca').select('status').eq('id', rotaId).maybeSingle();
    if (error) throw error;
    if (!data) throw new Error('Rota não encontrada.');
    if (data.status !== 'em_conferencia') {
        throw new Error('Esta rota já foi liberada — não dá mais para mexer nas paradas.');
    }
};

/** Fecha os buracos de `posicao` depois de tirar parada. Devolve a lista nova. */
const renumerarParadas = async (rotaId: string): Promise<Array<{ id: string; posicao: number }>> => {
    const { data, error } = await supabase
        .from('rota_cobranca_paradas').select('id, posicao').eq('rota_id', rotaId)
        .neq('status', 'removida').order('posicao');
    if (error) throw error;
    const atuais = (data ?? []).map((p: any) => ({ id: String(p.id), posicao: Number(p.posicao) }));
    const mudancas = renumerar(atuais);
    // Uma a uma: são poucas, e o PostgREST não tem UPDATE em lote com valores diferentes.
    for (const m of mudancas) {
        const { error: e } = await supabase
            .from('rota_cobranca_paradas').update({ posicao: m.posicao }).eq('id', m.id);
        if (e) throw e;
    }
    const nova = new Map(mudancas.map(m => [m.id, m.posicao]));
    return atuais.map(p => ({ id: p.id, posicao: nova.get(p.id) ?? p.posicao }));
};

/**
 * O que a conferência de uma parada deixa de valer quando as UCs dela mudam.
 *
 * Material e termo são das UCs — mudou o conjunto, a resposta dada antes pode
 * estar errada (ex.: "tem termo" valia para a UC que saiu). O endereço só cai
 * quando a principal muda, porque o pino conferido era o dela.
 */
const reabrirConferencia = (trocouEndereco: boolean) => ({
    material_situacao: null,
    material_faltando: null,
    material_justificativa: null,
    termo_situacao: null,
    termo_url: null,
    conferida_em: null,
    conferida_por: null,
    ...(trocouEndereco ? {
        endereco_situacao: null, endereco_lat: null, endereco_lng: null, endereco_label: null,
    } : {}),
});

/** Tira a parada inteira. Devolve as posições novas das que ficaram. */
export const tirarParadaDaRota = async (
    rotaId: string,
    paradaId: string,
): Promise<Array<{ id: string; posicao: number }>> => {
    await exigirEmConferencia(rotaId);
    const { error } = await supabase
        .from('rota_cobranca_paradas').delete().eq('id', paradaId).eq('rota_id', rotaId);
    if (error) throw error;
    return renumerarParadas(rotaId);
};

export type ResultadoTirarDaParada =
    | { tipo: 'parada_removida'; posicoes: Array<{ id: string; posicao: number }> }
    | { tipo: 'parada_atualizada'; campos: CamposGrupo & ReturnType<typeof reabrirConferencia> };

/**
 * Tira a parada de uma rota já liberada. Diferente de `tirarParadaDaRota` (que
 * apaga, na conferência), aqui a linha fica com quem tirou, quando e por quê —
 * `mapearRota` a esconde. O motivo vai só em `removida_motivo`: `resultado` tem
 * vocabulário fechado no CHECK e texto livre ali derrubava o UPDATE.
 *
 * Só sai parada pendente: a visitada já entrou na timeline do cliente.
 */
export const removerParada = async (
    paradaId: string,
    rotaId: string,
    motivo: string,
    userId: string
): Promise<Array<{ id: string; posicao: number }>> => {
    const texto = motivo.trim();
    if (!texto) throw new Error('Diga por que a parada está saindo da rota.');

    const { data, error } = await supabase.from('rota_cobranca_paradas').update({
        status: 'removida',
        removida_em: new Date().toISOString(),
        removida_por: userId,
        removida_motivo: texto,
    }).eq('id', paradaId).eq('rota_id', rotaId).eq('status', 'pendente').select('id');

    if (error) throw error;
    if (!data?.length) throw new Error('Esta parada já foi visitada ou removida. Atualize a tela.');
    return renumerarParadas(rotaId);
};

/**
 * Parada incluída à mão numa rota já liberada.
 *
 * Relê a UC pelo id em vez de confiar no objeto do seletor: cada seletor traz
 * um recorte de colunas, e o que faltava virava lixo em silêncio — `cpf_cnpj`
 * não existe na UC (a parada saía sem CPF e a visita não entrava na timeline) e
 * sem `outro_numero_uc` as faturas gravadas com o outro número sumiam do valor.
 */
export const adicionarParadaExtra = async (
    rotaId: string,
    ucId: number,
    userId: string
): Promise<void> => {
    const [{ data: uc, error: erroUc }, { data: ultima, error: erroPosicao }] = await Promise.all([
        supabase.from('unidades_consumidoras')
            .select('id, numero_uc, outro_numero_uc, nome_cliente, titular_fatura, cpf_cnpj_cliente')
            .eq('id', ucId).maybeSingle(),
        supabase.from('rota_cobranca_paradas')
            .select('posicao').eq('rota_id', rotaId).neq('status', 'removida')
            .order('posicao', { ascending: false }).limit(1),
    ]);
    if (erroUc) throw erroUc;
    if (erroPosicao) throw erroPosicao;
    const ucCodigo = uc?.numero_uc || uc?.outro_numero_uc;
    if (!uc || !ucCodigo) throw new Error('UC sem número no cadastro — não dá para cobrar sem ela.');

    // Valor congelado agora, como na montagem — senão o card já nasce com o
    // aviso de "o valor mudou desde que a rota foi montada".
    const faturas = await faturasAtuaisDaUc(ucCodigo, [uc.numero_uc, uc.outro_numero_uc]);

    // Mesma ordem da montagem (useInadimplenciaData): cadastro da UC e, sem ele,
    // o documento gravado na fatura — base migrada tem UC sem CPF.
    let cpf = normalizarCpfCnpj(uc.cpf_cnpj_cliente);
    if (!cpf && faturas.length > 0) {
        const { data } = await supabase.from('faturas').select('cliente_cpf_cnpj')
            .in('id', faturas.map(f => f.id)).not('cliente_cpf_cnpj', 'is', null).limit(1);
        cpf = normalizarCpfCnpj(data?.[0]?.cliente_cpf_cnpj);
    }

    const { error } = await supabase.from('rota_cobranca_paradas').insert({
        rota_id: rotaId,
        posicao: (ultima?.[0]?.posicao ?? 0) + 1,
        status: 'pendente',
        cpf_cnpj_cliente: cpf || null,
        ...camposDoGrupo([{
            unidade_consumidora_id: uc.id,
            uc_codigo: ucCodigo,
            cliente_nome: uc.nome_cliente || uc.titular_fatura || null,
            qtd_faturas: faturas.length,
            valor_total: faturas.reduce((s, f) => s + f.valor, 0),
        }]),
        adicionada_em: new Date().toISOString(),
        adicionada_por: userId,
    });

    if (error) throw error;
};

/** Tira UMA UC de uma parada que junta várias. Sendo a última, sai a parada. */
export const tirarUcDaRota = async (
    rotaId: string,
    parada: ParadaSalva,
    ucCodigo: string,
): Promise<ResultadoTirarDaParada> => {
    const r = tirarUcDaParada(parada, ucCodigo);
    if (r.tipo === 'apagar_parada') {
        return { tipo: 'parada_removida', posicoes: await tirarParadaDaRota(rotaId, parada.id) };
    }
    await exigirEmConferencia(rotaId);
    const campos = { ...r.campos, ...reabrirConferencia(r.trocouEndereco) };
    const { error } = await supabase.from('rota_cobranca_paradas').update(campos).eq('id', parada.id);
    if (error) throw error;
    return { tipo: 'parada_atualizada', campos };
};

/**
 * Junta as paradas do mesmo cliente numa só — para as rotas montadas antes da
 * junção automática. Devolve quantas paradas sumiram.
 *
 * Atualiza a que fica ANTES de apagar as outras: se algo falhar no meio, o pior
 * caso é uma UC aparecer duas vezes (visível e corrigível), nunca uma UC sumir.
 */
export const juntarParadasDoMesmoCliente = async (
    rotaId: string,
    paradas: ParadaSalva[],
): Promise<number> => {
    await exigirEmConferencia(rotaId);
    const juncoes = juncoesPendentes(paradas);
    let apagadas = 0;
    for (const j of juncoes) {
        const { error } = await supabase
            .from('rota_cobranca_paradas')
            .update({ ...j.campos, ...reabrirConferencia(false) })
            .eq('id', j.manter.id);
        if (error) throw error;
        const ids = j.apagar.map(p => p.id);
        const { error: e } = await supabase.from('rota_cobranca_paradas').delete().in('id', ids);
        if (e) throw e;
        apagadas += ids.length;
    }
    if (apagadas > 0) await renumerarParadas(rotaId);
    return apagadas;
};

/**
 * Apagar ou cancelar — a decisão é do dado, não de quem clica.
 *
 * Rota sem visita registrada é lixo de montagem (as duplicatas que apareceram na
 * lista do gestor) e pode sumir. Rota com visita não: a visita já entrou em
 * `cobranca_negociacao_historico` e alimenta a timeline do cliente; apagar a
 * parada deixaria a timeline apontando para uma rota que não existe mais.
 */
export const excluirOuCancelarRota = async (
    rotaId: string,
    userId: string,
    motivo?: string | null,
): Promise<'excluida' | 'cancelada'> => {
    const { count, error: erroConta } = await supabase
        .from('rota_cobranca_paradas')
        .select('id', { count: 'exact', head: true })
        .eq('rota_id', rotaId)
        .not('visitado_em', 'is', null);
    if (erroConta) throw erroConta;

    if ((count ?? 0) > 0) {
        const { error } = await supabase
            .from('rotas_cobranca')
            .update({
                status: 'cancelada',
                cancelada_em: new Date().toISOString(),
                cancelada_por: userId,
                cancelamento_motivo: motivo ?? null,
            })
            .eq('id', rotaId);
        if (error) throw error;
        return 'cancelada';
    }

    // As paradas caem por ON DELETE CASCADE (FK de 20260910121000).
    const { error } = await supabase.from('rotas_cobranca').delete().eq('id', rotaId);
    if (error) throw error;
    return 'excluida';
};

/** Quem pode receber uma rota de cobrança. A Central (montagem) e a tela de
 *  campo (transferência) usam a mesma lista — senão uma oferece quem a outra não. */
export const listarEquipeCobranca = async (): Promise<Array<{ id: string; full_name: string | null }>> => {
    const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('role', ['adm', 'gerente', 'analista', 'motoboy'])
        .order('full_name');
    if (error) throw error;
    return (data ?? []) as Array<{ id: string; full_name: string | null }>;
};

/**
 * Passa a rota para outra pessoa executar.
 *
 * Existe porque a rota ficava presa a quem foi escolhido na montagem: se essa
 * pessoa não pudesse ir, o jeito era montar tudo de novo — e a conferência junto.
 * Só troca quem EXECUTA; quem montou continua registrado, e as visitas já
 * feitas ficam no nome de quem as fez. Rota encerrada não muda de dono: o
 * histórico dela já está fechado.
 */
export const transferirRotaCobranca = async (
    rotaId: string,
    novoResponsavelId: string,
): Promise<void> => {
    const { data, error } = await supabase
        .from('rotas_cobranca')
        .update({ responsavel_id: novoResponsavelId })
        .eq('id', rotaId)
        .in('status', ['em_conferencia', 'planejada', 'em_execucao'])
        .select('id');
    if (error) throw error;
    if (!data?.length) throw new Error('Esta rota já foi encerrada — não dá mais para transferir.');
};

export interface FaturaEmAberto {
    id: string;
    mes_referencia: string | null;
    vencimento: string | null;
    valor: number;
    linha_digitavel_boleto: string | null;
    codigo_pix_boleto: string | null;
    url_boleto: string | null;
    url_boleto_combinado: string | null;
    pdf_boleto_combinado: string | null;
    pdf_fatura: string | null;
    num_cobranca_boleto: string | null;
    /** `faturas.classificacao`. 'AVULSO' é cobrança à parte (parcela de acordo,
     *  acerto, saldo de cancelamento) e não nasce de uma fatura da distribuidora
     *  — ver `ehCobrancaAvulsa` em `conferenciaRota`. */
    classificacao?: string | null;
    /** A UC da PARADA a que esta fatura pertence (o código pedido, não o gravado
     *  na fatura — a Equatorial usa dois números para a mesma unidade). É o que
     *  separa as faturas quando a parada junta mais de uma UC. */
    uc_ref?: string | null;
}

/**
 * O que a UC deve HOJE — relido na hora da visita.
 *
 * A parada guarda o valor congelado na geração da rota, que é o retrato do dia
 * em que ela foi montada. Entre montar e visitar, o cliente pode ter pago. O
 * cobrador não pode bater na porta cobrando valor errado, então a tela mostra os
 * dois e avisa quando diferem.
 *
 * O status vem de `derivarStatusBoleto`, nunca de `status_boleto_vigor` cru: o
 * campo está dessincronizado nos dois sentidos e traria boleto cancelado como
 * dívida.
 */
export const faturasAtuaisDaUc = async (
    ucCodigo: string,
    /**
     * Os outros números da MESMA unidade (`numero_uc` e `outro_numero_uc` do
     * cadastro). Os dois, não "o outro": a parada pode ter sido montada com
     * qualquer um deles, e passar só `outro_numero_uc` devolvia a mesma chave
     * quando a parada já usava esse número — as faturas gravadas com o
     * `numero_uc` sumiam da releitura. Aconteceu com o Eurivan: R$ 1.514,54 em
     * três faturas vencidas, gravadas como 307381301204, fora da conferência.
     */
    outrosNumeros?: string | null | Array<string | null | undefined>,
): Promise<FaturaEmAberto[]> => {
    // `uc_codigo_norm` é coluna GENERATED e é onde as chaves normalizadas casam;
    // filtrar por `uc_codigo` cru perderia a fatura gravada com a outra grafia.
    const outros = Array.isArray(outrosNumeros) ? outrosNumeros : [outrosNumeros];
    const chaves = Array.from(new Set(
        [ucCodigo, ...outros].map(n => normalizeUcCodigo(n)).filter(Boolean)));
    if (chaves.length === 0) return [];

    // A cobrança avulsa que junta duas unidades é gravada com os dois números no
    // mesmo campo ("10015106754/10039677999" depois de normalizado), e o `.in`
    // sozinho não a encontra — a parcela do acordo sumia da releitura do saldo.
    // Os curingas são ancorados na barra de propósito: `like '*10015106754*'`
    // casaria também uma UC que apenas contém esse número.
    const filtro = [
        `uc_codigo_norm.in.(${chaves.join(',')})`,
        ...chaves.flatMap(k => [`uc_codigo_norm.like.${k}/*`, `uc_codigo_norm.like.*/${k}`]),
    ].join(',');

    const { data, error } = await supabase
        .from('faturas')
        .select('id, uc_codigo, mes_referencia, vencimento_boleto, data_vencimento, valor_boleto_vigor, valor_emissao_boleto_unico, status_boleto_vigor, status_fatura, data_pgto_boleto, quitado_em, cancelado_em, linha_digitavel_boleto, codigo_pix_boleto, url_boleto, url_boleto_combinado, pdf_boleto_combinado, pdf_fatura, num_cobranca_boleto, classificacao, cobranca_agrupada')
        .eq('ambiente', 'producao')
        .in('status_fatura', STATUS_FATURA_COM_BOLETO as unknown as string[])
        .or(filtro);
    if (error) throw error;

    const agora = new Date();
    return (data || [])
        .filter((f: any) => derivarStatusBoleto(f, agora) === 'Vencido')
        .map((f: any) => ({
            id: String(f.id),
            mes_referencia: f.mes_referencia ?? null,
            vencimento: f.vencimento_boleto || f.data_vencimento || null,
            valor: valorDevido(f),
            linha_digitavel_boleto: f.linha_digitavel_boleto ?? null,
            codigo_pix_boleto: f.codigo_pix_boleto ?? null,
            url_boleto: f.url_boleto ?? null,
            url_boleto_combinado: f.url_boleto_combinado ?? null,
            pdf_boleto_combinado: f.pdf_boleto_combinado ?? null,
            pdf_fatura: f.pdf_fatura ?? null,
            num_cobranca_boleto: f.num_cobranca_boleto ?? null,
            classificacao: f.classificacao ?? null,
            uc_ref: ucCodigo,
        }))
        .sort((a, b) => String(a.vencimento || '').localeCompare(String(b.vencimento || '')));
};

/**
 * O que a parada inteira deve hoje: a UC principal e as agrupadas.
 *
 * `numerosDaUc` resolve os números da Equatorial de cada UC com cadastro — sem
 * eles a fatura gravada com a outra grafia some e a parada parece paga.
 */
export const faturasAtuaisDaParada = async (
    parada: ParadaAgrupavel,
    /** Os números do cadastro daquela UC — `[numero_uc, outro_numero_uc]`. */
    numerosDaUc: (ucId: number | null) => Array<string | null | undefined>,
): Promise<FaturaEmAberto[]> => {
    const porParada = await faturasAtuaisDasParadas([parada], numerosDaUc);
    return porParada.get(parada.id) || [];
};

/** Colunas da releitura das faturas das paradas: situação do boleto, meios de
 *  pagamento e a ligação com acordo de parcelamento. */
const COLUNAS_FATURA_PARADA =
    'id, uc_codigo, uc_codigo_norm, mes_referencia, vencimento_boleto, data_vencimento, valor_boleto_vigor, '
    + 'valor_emissao_boleto_unico, status_boleto_vigor, status_fatura, data_pgto_boleto, quitado_em, cancelado_em, '
    + 'linha_digitavel_boleto, codigo_pix_boleto, url_boleto, url_boleto_combinado, pdf_boleto_combinado, pdf_fatura, '
    + 'num_cobranca_boleto, classificacao, parcelamento_origem_id, cobranca_agrupada';

/** A UC normalizada casa com uma das chaves? A fatura avulsa que junta duas
 *  unidades grava "A/B": a chave vale na ponta, ancorada na barra — conter o
 *  número não basta. */
const casaChaveUc = (norm: string, chaves: string[]): boolean =>
    !!norm && chaves.some(k => norm === k || norm.startsWith(`${k}/`) || norm.endsWith(`/${k}`));

const paraFaturaEmAberto = (f: any, ucRef: string): FaturaEmAberto => ({
    id: String(f.id),
    mes_referencia: f.mes_referencia ?? null,
    vencimento: f.vencimento_boleto || f.data_vencimento || null,
    valor: valorDevido(f),
    linha_digitavel_boleto: f.linha_digitavel_boleto ?? null,
    codigo_pix_boleto: f.codigo_pix_boleto ?? null,
    url_boleto: f.url_boleto ?? null,
    url_boleto_combinado: f.url_boleto_combinado ?? null,
    pdf_boleto_combinado: f.pdf_boleto_combinado ?? null,
    pdf_fatura: f.pdf_fatura ?? null,
    num_cobranca_boleto: f.num_cobranca_boleto ?? null,
    classificacao: f.classificacao ?? null,
    uc_ref: ucRef,
});

const porVencimento = (a: { vencimento: string | null }, b: { vencimento: string | null }) =>
    String(a.vencimento || '').localeCompare(String(b.vencimento || ''));

/**
 * Todas as faturas com boleto das UCs de cada parada — pagas, vencidas, a vencer.
 *
 * A conferência abria com uma consulta POR PARADA (e por UC dentro dela): numa
 * rota de 25 paradas eram 25+ idas ao banco disparadas juntas, cada uma varrendo
 * as 7.5k faturas com boleto para devolver uma dúzia. Medido em 16/09/2026, cada
 * uma custava 1,4 s — a tela ficava parada em "Lendo a rota…".
 *
 * Aqui são duas consultas, independente do tamanho da rota:
 *
 *  1. `uc_codigo_norm in (…)` com as chaves de todas as UCs da rota. Casa pela
 *     coluna GENERATED, então usa índice e resolve o caso normal.
 *  2. As faturas de UC composta (`"A/B"`, a cobrança avulsa que junta duas
 *     unidades). São 31 em 7.540, e o `like '%/chave'` que as acha não usa
 *     índice — em vez de um curinga por chave, traz as 31 de uma vez e casa
 *     aqui. O filtro é o mesmo `^chave/` ou `/chave$` de antes, ancorado na
 *     barra de propósito: conter o número não basta.
 */
const lerFaturasDasParadas = async (
    paradas: ParadaAgrupavel[],
    numerosDaUc: (ucId: number | null) => Array<string | null | undefined>,
): Promise<Map<string, Array<{ f: any; ucRef: string }>>> => {
    const saida = new Map<string, Array<{ f: any; ucRef: string }>>();
    paradas.forEach(p => saida.set(p.id, []));
    if (paradas.length === 0) return saida;

    // Cada UC da rota com as chaves que a representam: a da parada e as do
    // cadastro. Mesma resolução de `faturasAtuaisDaUc` — sem os dois números a
    // fatura gravada com a outra grafia some (caso Eurivan, R$ 1.514,54).
    const alvos = paradas.flatMap(p => ucsDaParada(p).map(u => ({
        paradaId: p.id,
        ucRef: u.uc_codigo,
        chaves: Array.from(new Set(
            [u.uc_codigo, ...numerosDaUc(u.unidade_consumidora_id)]
                .map(n => normalizeUcCodigo(n))
                .filter(Boolean) as string[])),
    }))).filter(a => a.chaves.length > 0);
    if (alvos.length === 0) return saida;

    const todasAsChaves = Array.from(new Set(alvos.flatMap(a => a.chaves)));

    const base = () => supabase
        .from('faturas')
        .select(COLUNAS_FATURA_PARADA)
        .eq('ambiente', 'producao')
        .in('status_fatura', STATUS_FATURA_COM_BOLETO as unknown as string[]);

    // `.in` com muitas chaves vira URL longa; 300 por bloco é folgado para uma
    // rota (o teto de paradas é 24) e seguro para quem chamar com mais.
    const blocos: string[][] = [];
    for (let i = 0; i < todasAsChaves.length; i += 300) blocos.push(todasAsChaves.slice(i, i + 300));

    const [exatas, compostas] = await Promise.all([
        Promise.all(blocos.map(async bloco => {
            const { data, error } = await base().in('uc_codigo_norm', bloco);
            if (error) throw error;
            return data || [];
        })).then(r => r.flat()),
        base().like('uc_codigo_norm', '%/%').then(({ data, error }) => {
            if (error) throw error;
            return data || [];
        }),
    ]);
    const todas = [...exatas, ...compostas] as any[];

    // Uma fatura pode casar com mais de uma UC da MESMA parada quando o cadastro
    // está duplicado (Super Gelo tem duas linhas para a mesma unidade): conta uma
    // vez por parada, guardando a primeira `uc_ref` que casou.
    const vistasPorParada = new Map<string, Set<string>>();
    paradas.forEach(p => vistasPorParada.set(p.id, new Set()));

    for (const alvo of alvos) {
        const vistas = vistasPorParada.get(alvo.paradaId)!;
        for (const f of todas) {
            if (!casaChaveUc(String(f.uc_codigo_norm ?? ''), alvo.chaves)) continue;
            const id = String(f.id);
            if (vistas.has(id)) continue;
            vistas.add(id);
            saida.get(alvo.paradaId)!.push({ f, ucRef: alvo.ucRef });
        }
    }
    return saida;
};

/** O que cada parada da rota deve HOJE, em duas consultas para a rota inteira —
 *  ver `lerFaturasDasParadas`. Mesmo resultado de `faturasAtuaisDaParada`. */
export const faturasAtuaisDasParadas = async (
    paradas: ParadaAgrupavel[],
    numerosDaUc: (ucId: number | null) => Array<string | null | undefined>,
): Promise<Map<string, FaturaEmAberto[]>> => {
    const lidas = await lerFaturasDasParadas(paradas, numerosDaUc);
    const agora = new Date();
    const saida = new Map<string, FaturaEmAberto[]>();
    lidas.forEach((lista, paradaId) => saida.set(paradaId, lista
        .filter(({ f }) => derivarStatusBoleto(f, agora) === 'Vencido')
        .map(({ f, ucRef }) => paraFaturaEmAberto(f, ucRef))
        .sort(porVencimento)));
    return saida;
};

/** Um boleto da UC que não está vencido: pago, parcelado, quitado, cancelado ou a vencer. */
export interface FaturaDaParada extends FaturaEmAberto {
    /** Situação de hoje, pela regra única (`derivarStatusBoleto`). */
    status: StatusBoleto;
    pago_em: string | null;
    data_pgto_boleto: string | null;
    quitado_em: string | null;
}

export interface FaturasDaParadaHoje {
    /** O que a parada deve hoje — a mesma lista de `faturasAtuaisDaParada`. */
    vencidas: FaturaEmAberto[];
    /** Os outros boletos das UCs nos últimos 12 meses: os já vencidos do mais
     *  novo para o mais velho, depois os a vencer. É o que responde "ele diz que
     *  pagou — pagou?". */
    outras: FaturaDaParada[];
    /** Situação de cada boleto lido, por id — o resumo dos acordos usa. */
    porId: Map<string, DadosStatusBoleto>;
    /** Acordos que geraram ou parcelaram algum destes boletos. */
    acordoIds: string[];
}

/** Meses de boletos pagos e resolvidos que o card mostra. */
const MESES_DE_HISTORICO = 12;

/**
 * O que a parada deve hoje E o que ela já pagou — pedido de 30/09/2026: o
 * cobrador abria Faturas em outra aba para saber se o boleto tinha sido pago.
 *
 * É a mesma leitura de `faturasAtuaisDaParada`, sem jogar fora o que não está
 * vencido. Boleto sem cobrança (valor zero, fora do pipeline) não entra: não é
 * dívida nem pagamento.
 */
export const faturasDaParadaHoje = async (
    parada: ParadaAgrupavel,
    numerosDaUc: (ucId: number | null) => Array<string | null | undefined>,
): Promise<FaturasDaParadaHoje> => {
    const lidas = (await lerFaturasDasParadas([parada], numerosDaUc)).get(parada.id) || [];
    const agora = new Date();
    const inicio = new Date(agora);
    inicio.setMonth(inicio.getMonth() - MESES_DE_HISTORICO);
    const desde = isoLocal(inicio);

    const vencidas: FaturaEmAberto[] = [];
    const outras: FaturaDaParada[] = [];
    const porId = new Map<string, DadosStatusBoleto>();
    const acordoIds = new Set<string>();
    for (const { f, ucRef } of lidas) {
        porId.set(String(f.id), f);
        if (f.parcelamento_origem_id) acordoIds.add(String(f.parcelamento_origem_id));
        const status = derivarStatusBoleto(f, agora);
        if (status === 'Vencido') { vencidas.push(paraFaturaEmAberto(f, ucRef)); continue; }
        if (STATUS_SEM_COBRANCA.includes(status)) continue;
        const vencimento = String(f.vencimento_boleto || f.data_vencimento || '');
        if (vencimento && vencimento.slice(0, 10) < desde) continue;
        outras.push({
            ...paraFaturaEmAberto(f, ucRef),
            status,
            pago_em: f.data_pgto_boleto || f.quitado_em || null,
            data_pgto_boleto: f.data_pgto_boleto ?? null,
            quitado_em: f.quitado_em ?? null,
        });
    }
    vencidas.sort(porVencimento);
    // Primeiro o que já venceu, do mais novo para o mais velho — é onde está o
    // pagamento que o cliente diz ter feito. Depois o que ainda vai vencer, do
    // mais próximo ao mais longe: as parcelas futuras de um acordo não podem
    // empurrar os pagamentos para fora da lista curta do card.
    const hojeIso = isoLocal(agora);
    const jaVenceu = (f: FaturaDaParada) => !f.vencimento || f.vencimento.slice(0, 10) <= hojeIso;
    outras.sort((a, b) => {
        if (jaVenceu(a) !== jaVenceu(b)) return jaVenceu(a) ? -1 : 1;
        return jaVenceu(a) ? porVencimento(b, a) : porVencimento(a, b);
    });
    return { vencidas, outras, porId, acordoIds: [...acordoIds] };
};

/** Colunas do acordo que o card da parada mostra. */
const COLUNAS_ACORDO_PARADA =
    'id, status, created_at, total_acordo, valor_total, entrada_valor, entrada_vencimento, entrada_fatura_id, '
    + 'entrada_paga_em, entrada_envio, termo_id_autentique, termo_url, termo_enviado_em, termo_assinado_em, '
    + 'termo_envio_erro, termo_entrega, expira_em, cancelado_em, faturas_origem_snapshot, '
    + 'parcelamento_parcelas(numero_parcela, tipo, valor, data_vencimento, status, fatura_id)';

export interface AcordosDaParada {
    /** Mais novo primeiro. Inclui os encerrados — a tela decide o que mostrar. */
    acordos: Array<AcordoLido & Record<string, unknown>>;
    /** Situação dos boletos lidos (os da UC + entrada e parcelas de outra UC). */
    faturas: Map<string, DadosStatusBoleto>;
}

/**
 * Os acordos de parcelamento das UCs da parada.
 *
 * Três caminhos, porque cada fase do acordo deixa um rastro diferente:
 *  - vínculo com o cadastro da UC (`unidade_consumidora_id`);
 *  - boletos que o acordo gerou (entrada, parcelas) ou parcelou — o
 *    `parcelamento_origem_id` das faturas lidas;
 *  - a proposta que ainda não carimbou nada, que só se liga às faturas pelo
 *    snapshot. São poucas no sistema inteiro (dezenas), então vêm todas e o
 *    casamento é aqui.
 */
export const acordosDaParada = async (
    parada: ParadaAgrupavel,
    lidas: Pick<FaturasDaParadaHoje, 'porId' | 'acordoIds'>,
): Promise<AcordosDaParada> => {
    const ucIds = Array.from(new Set(ucsDaParada(parada)
        .map(u => u.unidade_consumidora_id)
        .filter((v): v is number => v != null)));

    const { data: propostas, error: erroPropostas } = await supabase
        .from('parcelamentos')
        .select('id, faturas_origem_snapshot')
        .in('status', [...ESTADOS_PENDENTES]);
    if (erroPropostas) throw erroPropostas;
    const ids = new Set(lidas.acordoIds);
    for (const p of (propostas || []) as any[]) {
        const snapshot = Array.isArray(p.faturas_origem_snapshot) ? p.faturas_origem_snapshot : [];
        if (snapshot.some((s: any) => lidas.porId.has(String(s?.id)))) ids.add(String(p.id));
    }

    const filtros = [
        ids.size > 0 ? `id.in.(${[...ids].join(',')})` : null,
        ucIds.length > 0 ? `unidade_consumidora_id.in.(${ucIds.join(',')})` : null,
    ].filter(Boolean);
    if (filtros.length === 0) return { acordos: [], faturas: new Map(lidas.porId) };

    const { data, error } = await supabase
        .from('parcelamentos')
        .select(COLUNAS_ACORDO_PARADA)
        .or(filtros.join(','))
        .order('created_at', { ascending: false })
        .limit(20);
    if (error) throw error;
    const acordos = (data || []) as unknown as AcordosDaParada['acordos'];

    // Boleto da entrada ou de parcela que saiu em outra UC (acordo que junta
    // UCs sai na da fatura mais recente): não veio com as faturas desta parada.
    const faltam = new Set<string>();
    const falta = (id: unknown) => { if (id != null && !lidas.porId.has(String(id))) faltam.add(String(id)); };
    for (const a of acordos) {
        falta(a.entrada_fatura_id);
        (a.parcelamento_parcelas || []).forEach(p => falta(p.fatura_id));
    }
    const faturas = new Map(lidas.porId);
    if (faltam.size > 0) {
        const { data: extras, error: erroExtras } = await supabase
            .from('faturas')
            .select('id, status_boleto_vigor, status_fatura, valor_boleto_vigor, valor_emissao_boleto_unico, vencimento_boleto, data_vencimento, data_pgto_boleto, quitado_em, num_cobranca_boleto')
            .in('id', [...faltam]);
        if (erroExtras) throw erroExtras;
        (extras || []).forEach((f: any) => faturas.set(String(f.id), f));
    }
    return { acordos, faturas };
};

/** Estados em que o acordo ainda pede atenção: proposta, ativo e em atraso. */
const ESTADOS_ACORDO_VIVO = [...ESTADOS_PENDENTES, 'ativo', 'em_atraso'];

export interface AcordoVivoLeve {
    id: string;
    status: string;
    unidade_consumidora_id: number | null;
    uc_codigo_snapshot: string | null;
    faturas_origem_snapshot: Array<{ id: string | number }> | null;
}

/** Os acordos vivos do sistema inteiro — dezenas. Uma consulta para a rota toda;
 *  quem é de qual parada é `acordosVivosPorParada`. */
export const lerAcordosVivos = async (): Promise<AcordoVivoLeve[]> => {
    const { data, error } = await supabase
        .from('parcelamentos')
        .select('id, status, unidade_consumidora_id, uc_codigo_snapshot, faturas_origem_snapshot')
        .in('status', ESTADOS_ACORDO_VIVO);
    if (error) throw error;
    return (data || []) as AcordoVivoLeve[];
};

/**
 * Os acordos vivos de cada parada, para a etiqueta do card FECHADO — quem rola a
 * lista vê "Em acordo" sem abrir a parada.
 *
 * Casa pelo cadastro da UC, pelo número da UC do acordo (o da fatura de
 * referência) e pelas faturas vencidas que estão no snapshot da proposta.
 */
export const acordosVivosPorParada = (
    acordos: AcordoVivoLeve[],
    paradas: ParadaAgrupavel[],
    numerosDaUc: (ucId: number | null) => Array<string | null | undefined>,
    vencidasPorParada: Map<string, FaturaEmAberto[]>,
): Map<string, AcordoVivoLeve[]> => {
    const saida = new Map<string, AcordoVivoLeve[]>();
    if (acordos.length === 0) return saida;
    for (const p of paradas) {
        const ucs = ucsDaParada(p);
        const ids = new Set(ucs.map(u => u.unidade_consumidora_id).filter(v => v != null));
        const chaves = Array.from(new Set(ucs
            .flatMap(u => [u.uc_codigo, ...numerosDaUc(u.unidade_consumidora_id)])
            .map(n => normalizeUcCodigo(n))
            .filter(Boolean)));
        const vencidas = new Set((vencidasPorParada.get(p.id) || []).map(f => String(f.id)));
        const achados = acordos.filter(a =>
            (a.unidade_consumidora_id != null && ids.has(a.unidade_consumidora_id))
            || casaChaveUc(normalizeUcCodigo(a.uc_codigo_snapshot), chaves)
            || (Array.isArray(a.faturas_origem_snapshot)
                && a.faturas_origem_snapshot.some(s => vencidas.has(String(s?.id)))));
        if (achados.length > 0) saida.set(p.id, achados);
    }
    return saida;
};

/**
 * O termo de adesão de cada parada — o contrato que encerra o "eu nunca assinei".
 *
 * Busca sob demanda, na hora de montar a mensagem, e não junto da carga da
 * Central: aquela consulta já varre 1.800 UCs e só uma fração vira parada de
 * rota. Mesmo motivo de `ultimaTratativaPorCpf`.
 *
 * São três fontes, medidas em 10/09/2026 sobre as 345 UCs com fatura vencida e
 * cadastro: `unidades_consumidoras.url_termo` (8), a negociação vinculada por
 * `negociacao_id` (44 — e todas as 44 têm termo) e a negociação de mesmo
 * CPF/CNPJ (mais 9). Somam 53, ou 15% — por isso a linha só sai quando existe.
 *
 * O casamento por CPF usa só dígitos, que é como 1.256 das 1.407 negociações
 * estão gravadas; as 151 com pontuação ficam de fora deste caminho e continuam
 * cobertas pelos outros dois.
 */
/** O mínimo para achar o termo. Estruturalmente satisfeito por `UcInadimplente` e
 *  também pela parada já salva, que é o que a conferência tem em mãos. */
export interface AlvoTermo {
    chave: string;
    ucId: number | null;
    cpfCnpj: string | null;
}

export const termosPorUc = async (ucs: AlvoTermo[]): Promise<Map<string, TermoAdesao>> => {
    const achados = new Map<string, TermoAdesao>();
    if (ucs.length === 0) return achados;

    const digitos = (v: unknown) => String(v ?? '').replace(/\D/g, '');
    const emBlocos = async <T>(itens: T[], tamanho: number, fn: (bloco: T[]) => Promise<void>) => {
        for (let i = 0; i < itens.length; i += tamanho) await fn(itens.slice(i, i + tamanho));
    };

    // 1) Cadastro da UC: termo próprio e o vínculo com a negociação.
    const termoDaUc = new Map<number, ItemTermo[] | null>();
    const negociacaoDaUc = new Map<number, number>();
    const ids = Array.from(new Set(ucs.map(u => u.ucId).filter((v): v is number => v != null)));
    await emBlocos(ids, 200, async bloco => {
        const { data, error } = await supabase
            .from('unidades_consumidoras')
            .select('id, url_termo, negociacao_id')
            .in('id', bloco);
        if (error) { console.error('termosPorUc (uc)', error.message); return; }
        (data ?? []).forEach((r: any) => {
            termoDaUc.set(r.id, r.url_termo ?? null);
            if (r.negociacao_id != null) negociacaoDaUc.set(r.id, Number(r.negociacao_id));
        });
    });

    // 2) Negociações vinculadas.
    const termoDaNegociacao = new Map<number, ItemTermo[] | null>();
    const negIds = Array.from(new Set(negociacaoDaUc.values()));
    await emBlocos(negIds, 200, async bloco => {
        const { data, error } = await supabase
            .from('negociacoes').select('id, url_termo').in('id', bloco);
        if (error) { console.error('termosPorUc (negociacao)', error.message); return; }
        (data ?? []).forEach((r: any) => termoDaNegociacao.set(Number(r.id), r.url_termo ?? null));
    });

    // 3) Negociação de mesmo CPF/CNPJ, para quem não tem vínculo.
    const termoPorCpf = new Map<string, ItemTermo[] | null>();
    const cpfs = Array.from(new Set(
        ucs.map(u => digitos(u.cpfCnpj)).filter(c => c.length >= 11)));
    await emBlocos(cpfs, 100, async bloco => {
        const { data, error } = await supabase
            .from('negociacoes').select('cpf_cnpj, url_termo').in('cpf_cnpj', bloco);
        if (error) { console.error('termosPorUc (cpf)', error.message); return; }
        (data ?? []).forEach((r: any) => {
            const c = digitos(r.cpf_cnpj);
            // Só grava o primeiro com termo: o mesmo CPF pode ter várias
            // negociações e as sem termo não devem apagar a que tem.
            if (!termoPorCpf.get(c) && Array.isArray(r.url_termo) && r.url_termo.length > 0) {
                termoPorCpf.set(c, r.url_termo);
            }
        });
    });

    ucs.forEach(uc => {
        const idNeg = uc.ucId != null ? negociacaoDaUc.get(uc.ucId) : undefined;
        const termo = termoMaisRelevante(
            uc.ucId != null ? termoDaUc.get(uc.ucId) : null,
            idNeg != null ? termoDaNegociacao.get(idNeg) : null,
            termoPorCpf.get(digitos(uc.cpfCnpj)) ?? null,
        );
        if (termo) achados.set(uc.chave, termo);
    });

    return achados;
};
